import type { ScenarioKpis } from './api'

/** Hacia dónde es mejor que se mueva un número. */
export type Direction = 'higher' | 'lower' | 'neutral'

export interface KpiRow {
  key: keyof ScenarioKpis
  a: number
  b: number
  delta: number
  direction: Direction
  /** Cuál gana en esta fila, o empate. En una fila neutral nadie gana. */
  better: 'a' | 'b' | 'same'
}

/**
 * Qué mide cada número y hacia dónde es mejor.
 *
 * `utilization` va como neutral a propósito: con plantilla abundante un uso bajo no es un
 * defecto, y pintarlo de rojo empujaría al planner a exprimir unidades sin que nadie se lo haya
 * pedido. `expectedVisits` también, porque es el denominador, no un resultado.
 */
const DIRECTION: Record<keyof ScenarioKpis, Direction> = {
  coverage: 'higher',
  riskWeightedCoverage: 'higher',
  visitRating: 'higher',
  visits: 'higher',
  expectedVisits: 'neutral',
  utilization: 'neutral',
  hotspotsNotServed: 'lower',
  crossDivisionVisits: 'lower',
}

/**
 * El orden en que se leen: primero lo que decide, después el contexto.
 *
 * Encabeza la cobertura ponderada por riesgo porque **es la función objetivo**: lo que el
 * optimizador intenta maximizar (`docs/funcion-objetivo.md`). Las demás filas son diagnóstico.
 * Iba segunda, después de la cobertura simple, y eso hacía que quien compara dos escenarios
 * leyera primero un número que el motor no persigue.
 */
const ORDER: (keyof ScenarioKpis)[] = [
  'riskWeightedCoverage', 'coverage', 'visitRating',
  'visits', 'expectedVisits', 'hotspotsNotServed', 'crossDivisionVisits', 'utilization',
]

/** La fila que es la función objetivo; el resto acompaña. */
export const OBJECTIVE: keyof ScenarioKpis = 'riskWeightedCoverage'

export function compareKpis(a: ScenarioKpis, b: ScenarioKpis): KpiRow[] {
  return ORDER.map((key) => {
    const izquierda = a[key]
    const derecha = b[key]
    const delta = derecha - izquierda
    const direction = DIRECTION[key]
    let better: 'a' | 'b' | 'same' = 'same'
    if (direction !== 'neutral' && delta !== 0) {
      const sube = delta > 0
      better = (direction === 'higher') === sube ? 'b' : 'a'
    }
    return { key, a: izquierda, b: derecha, delta, direction, better }
  })
}

export interface PolicyDifference {
  key: string
  a: string
  b: string
}

/**
 * En qué se diferencian dos políticas.
 *
 * Devuelve **solo lo que cambia**, no las veinte perillas. Comparar dos escenarios es entender
 * por qué salieron distintos, y una lista donde diecisiete filas dicen lo mismo entierra las tres
 * que importan.
 *
 * Una perilla que existe en uno y no en el otro —un escenario viejo, corrido con una versión
 * anterior del motor— cuenta como diferencia y se marca con una raya, en vez de desaparecer.
 */
export function policyDifferences(
  a: Record<string, string>,
  b: Record<string, string>,
): PolicyDifference[] {
  const claves = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort()
  return claves
    .filter((k) => (a[k] ?? '—') !== (b[k] ?? '—'))
    .map((k) => ({ key: k, a: a[k] ?? '—', b: b[k] ?? '—' }))
}

/**
 * El nombre legible de una perilla.
 *
 * Si no hay traducción, se parte el camelCase en palabras en vez de mostrar la clave cruda: una
 * perilla nueva del optimizador aparece en la comparación el mismo día que se agrega, legible
 * aunque todavía no esté traducida.
 */
export function humanize(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // Las perillas del optimizador vienen en camelCase y los parámetros del motor de hotspots en
    // snake_case: los dos se leen igual de mal en crudo.
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
}
